import { useState } from 'react'
import { Link } from 'react-router-dom'

const WhatsAppIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.04 2A9.9 9.9 0 0 0 2.13 11.9c0 1.75.46 3.45 1.32 4.95L2 22l5.3-1.38a9.9 9.9 0 0 0 4.74 1.2A9.9 9.9 0 0 0 22 11.9 9.9 9.9 0 0 0 12.04 2Z"/>
  </svg>
)

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ marginRight: '8px' }}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z"/>
  </svg>
)

export default function Contact() {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    phone: '',
    message: ''
  })

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      const response = await fetch('/api/demo-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })
      if (!response.ok) throw new Error('Failed to submit')
      setIsSubmitted(true)
    } catch {
      // Handle error silently for now
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      {/* Contact Form / Success State */}
      <section style={{ padding: 'var(--space-8) 20px' }}>
        {isSubmitted ? (
          <div
            className="blueprint"
            style={{
              maxWidth: '520px',
              margin: 'var(--space-8) auto',
              padding: 'var(--space-8)',
              textAlign: 'center'
            }}
          >
            <i className="corner tl"></i>
            <i className="corner tr"></i>
            <i className="corner bl"></i>
            <i className="corner br"></i>
            <span
              style={{
                width: '58px',
                height: '58px',
                display: 'inline-grid',
                placeItems: 'center',
                background: 'var(--color-accent)',
                color: 'var(--color-bg)',
                marginBottom: '16px'
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5"/>
              </svg>
            </span>
            <h2 style={{ margin: '0 0 8px' }}>We'll be in touch today.</h2>
            <p style={{ fontSize: '15px', color: 'var(--color-neutral-800)', margin: '0 0 20px' }}>
              Thanks, {formData.name || 'there'}. We've got your details and will message you on WhatsApp to set up your demo. Prefer now?
            </p>
            <a
              href="https://wa.me/233000000000"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '9px',
                background: '#25D366',
                color: '#062b16',
                border: 0,
                padding: '13px 20px',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                fontSize: '15px',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              <WhatsAppIcon size={18} />
              Open WhatsApp now
            </a>
            <div style={{ marginTop: '18px' }}>
              <Link to="/" style={{ fontSize: '13px', color: 'var(--color-accent)', textDecoration: 'none' }}>
                &larr; Back to home
              </Link>
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--space-8)',
              maxWidth: '960px',
              margin: '0 auto',
              alignItems: 'flex-start'
            }}
          >
            {/* Left: Copy + Quick Actions */}
            <div style={{ flex: 1, minWidth: '280px' }}>
              <span
                style={{
                  fontSize: '12px',
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: 'var(--color-accent)'
                }}
              >
                Book a demo
              </span>
              <h1 style={{ fontSize: 'clamp(28px, 4.5vw, 40px)', margin: '8px 0 14px', lineHeight: 1.05 }}>
                Let's set you up.
              </h1>
              <p style={{ fontSize: '16px', color: 'var(--color-neutral-800)', maxWidth: '400px', margin: '0 0 24px' }}>
                Tell us a little about your business. We'll reach out on WhatsApp, show you around, and get you running — usually the same day.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '340px' }}>
                <a
                  href="https://wa.me/233000000000"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '9px',
                    background: '#25D366',
                    color: '#062b16',
                    border: 0,
                    padding: '15px 20px',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    fontSize: '16px',
                    cursor: 'pointer',
                    textDecoration: 'none'
                  }}
                >
                  <WhatsAppIcon />
                  Chat on WhatsApp now
                </a>
                <a
                  href="tel:+233000000000"
                  className="btn btn-secondary"
                  style={{
                    minHeight: '52px',
                    fontSize: '16px',
                    textDecoration: 'none',
                    color: 'var(--color-text)'
                  }}
                >
                  <PhoneIcon />
                  Call 030 000 0000
                </a>
              </div>
            </div>

            {/* Right: Blueprint Form Card */}
            <div
              className="blueprint"
              style={{ flex: 1, minWidth: '290px', padding: 'var(--space-6)' }}
            >
              <i className="corner tl"></i>
              <i className="corner tr"></i>
              <i className="corner bl"></i>
              <i className="corner br"></i>
              <h3 style={{ margin: '0 0 var(--space-4)' }}>Or leave your details</h3>
              <form onSubmit={handleSubmit}>
                <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
                  <label>Your name</label>
                  <input
                    className="input"
                    placeholder="Ama Owusu"
                    style={{ minHeight: '46px' }}
                    required
                    value={formData.name}
                    onChange={handleChange('name')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
                  <label>Business name</label>
                  <input
                    className="input"
                    placeholder="Ama's Chop Bar"
                    style={{ minHeight: '46px' }}
                    required
                    value={formData.businessName}
                    onChange={handleChange('businessName')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field" style={{ marginBottom: 'var(--space-3)' }}>
                  <label>Phone / WhatsApp</label>
                  <input
                    className="input"
                    placeholder="024 000 0000"
                    inputMode="tel"
                    style={{ minHeight: '46px' }}
                    required
                    value={formData.phone}
                    onChange={handleChange('phone')}
                    disabled={isSubmitting}
                  />
                </div>
                <div className="field" style={{ marginBottom: 'var(--space-4)' }}>
                  <label>Anything to add? (optional)</label>
                  <textarea
                    className="input"
                    placeholder="Restaurant, about 6 staff…"
                    value={formData.message}
                    onChange={handleChange('message')}
                    disabled={isSubmitting}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-block"
                  style={{ minHeight: '52px', fontSize: '16px' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Request my demo'}
                </button>
                <p className="text-muted" style={{ fontSize: '12px', margin: '12px 0 0', textAlign: 'center' }}>
                  No cost, no commitment. We'll never share your number.
                </p>
              </form>
            </div>
          </div>
        )}
      </section>

      {/* What to Expect Section */}
      <section
        id="what-to-expect"
        style={{
          padding: 'var(--space-8) 20px',
          borderTop: '1px solid var(--color-divider)',
          background: 'var(--color-accent-100)'
        }}
      >
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 34px)', margin: '0 0 var(--space-6)', textAlign: 'center' }}>
            What to Expect from Your Demo
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
            {[
              { n: '1', title: 'Quick Setup Call', body: 'A 15-minute call to understand your business and what you need.' },
              { n: '2', title: 'Personalized Demo', body: 'We show you exactly how TALLY works for businesses like yours.' },
              { n: '3', title: 'Get Started Fast', body: 'If you like what you see, we can have you up and running the same day.' }
            ].map((step) => (
              <div
                key={step.n}
                className="blueprint"
                style={{ padding: 'var(--space-4)', display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}
              >
                <i className="corner tl"></i>
                <i className="corner tr"></i>
                <i className="corner bl"></i>
                <i className="corner br"></i>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--color-accent-100)',
                    color: 'var(--color-accent-800)',
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 600,
                    flexShrink: 0
                  }}
                >
                  {step.n}
                </div>
                <div>
                  <h4 style={{ margin: '0 0 4px' }}>{step.title}</h4>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--color-neutral-800)' }}>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
